import { Injectable, inject, DestroyRef } from '@angular/core';
import { BehaviorSubject, Observable, timer, of, switchMap, map, catchError, filter } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppConstants } from '../app.constants';

@Injectable({
    providedIn: 'root'
})
export class ConnectivityService {
    private readonly httpClient = inject(HttpClient);
    private readonly destroyRef = inject(DestroyRef);

    /**
     * Internal subject that always holds the latest boolean:
     *   true  = “online”
     *   false = “offline”
     */
    private readonly connectivitySubject = new BehaviorSubject<boolean>(true);

    /**
     * Emits only when we become (or confirm) online.
     * Every time connectivitySubject emits `true`, online$ fires with `true`.
     */
    public readonly online$: Observable<boolean> = this.connectivitySubject.pipe(filter((isOnline) => isOnline === true));

    /**
     * Emits only when we become (or confirm) offline.
     * Every time connectivitySubject emits `false`, offline$ fires with `false`.
     */
    public readonly offline$: Observable<boolean> = this.connectivitySubject.pipe(filter((isOnline) => isOnline === false));

    /** How often to re-check (ms). */
    private readonly checkIntervalMs = 2000;

    /** URL to hit for a simple “ping” */
    private readonly heartbeatUrl = AppConstants.HEARTBEAT_URL;

    constructor() {
        this.startMonitoring();
    }

    /** Immediately check once, then set up a periodic timer. */
    private startMonitoring(): void {
        // Initial, immediate check:
        this.checkConnectivity().subscribe();

        // Every checkIntervalMs milliseconds afterward:
        timer(this.checkIntervalMs, this.checkIntervalMs)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                switchMap(() => this.checkConnectivity())
            )
            .subscribe();
    }

    /**
     * perform a fetch() to the heartbeatUrl.
     * - Success (2xx) → updateStatus(true)
     * - Failure (network error, timeout, non-2xx) → updateStatus(false)
     */
    private checkConnectivity(): Observable<void> {
        return this.httpClient
            .get(this.heartbeatUrl, {
                responseType: 'text'
            })
            .pipe(
                map(() => {
                    // If we reach here, the request succeeded (200 or 204).
                    this.updateStatus(true);
                }),
                catchError(() => {
                    // Any failure (DNS, timeout, CORS, etc.) → treat as offline.
                    this.updateStatus(false);
                    return of(undefined);
                })
            );
    }

    /**
     * Only emit when the boolean actually changes.
     * We re-enter the Angular zone here so that subscribers update properly.
     */
    private updateStatus(isOnline: boolean): void {
        if (this.connectivitySubject.value !== isOnline) {
            this.connectivitySubject.next(isOnline);
        }
    }
}
